import { Chip, Collapse, Theme, useMediaQuery } from '@mui/material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../useLayoutContext';

function DrawerList() {
  const { drawerSections, setDrawerOpen } = useLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  const matchDownSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState<Record<string, boolean>>();

  const handleClick = (path: string, isCollapsible = false) => {
    if (path === location.pathname) return;

    if (isCollapsible) {
      setOpen({ ...open, [path]: !open?.[path] });
    } else {
      if (path.startsWith('#')) {
        navigate(location.pathname + location.search + path);
      } else {
        navigate(path);
      }
    }

    if (!isCollapsible && matchDownSM) {
      setDrawerOpen(false);
    }
  };

  // TODO: link the drawer to the current section
  useEffect(() => {
    // open the first section by default
    if (drawerSections.length) {
      setOpen({ [location.pathname]: true });
    }
  }, [drawerSections, location.pathname]);

  return (
    <List
      sx={{ width: '100%', height: '100%' }}
      disablePadding
      component="nav"
    >
      {drawerSections.map((system) => (
        <React.Fragment key={system.label}>
          <ListItemButton
            onClick={() => handleClick(system.path, !!system.children)}
            selected={system.path === location.pathname}
          >
            {system.icon && <ListItemIcon>{system.icon}</ListItemIcon>}
            <ListItemText primary={system.label} sx={{ textTransform: 'capitalize' }} />
            {system.extra &&
              <Chip
                label={system.extra}
                color="inspectify_flag"
                variant="filled"
                sx={{ ml: 1, fontWeight: 'bold' }}
              />
            }
          </ListItemButton>

          {system.children && (
            <Collapse in={open?.[system.path]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {system.children.map((subsystem) => (
                  <React.Fragment key={subsystem.label}>
                    <ListItemButton
                      onClick={() => handleClick(subsystem.path, !!subsystem.children)}
                      selected={subsystem.path === location.pathname}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText primary={subsystem.label} sx={{ textTransform: 'capitalize', width: '100%' }} />
                      {subsystem.extra && (
                        <Chip
                          label={subsystem.extra}
                          color="inspectify_flag"
                          variant="filled"
                          sx={{ ml: 1, fontWeight: 'bold' }}
                        />
                      )}
                    </ListItemButton>
                    {subsystem.children && (
                      <Collapse in={open?.[subsystem.path]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {subsystem.children?.map((issue) => (
                            <ListItemButton
                              key={issue.label}
                              onClick={() => handleClick(issue.path)}
                              selected={issue.path === location.pathname}
                              sx={{ pl: 2 }}
                            >
                              {issue.icon && <ListItemIcon sx={{ minWidth: 'auto', px: 2 }}>{issue.icon}</ListItemIcon>}
                              <ListItemText primary={issue.label} sx={{ textTransform: 'capitalize' }} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      ))}
    </List>
  );
}

export default React.memo(DrawerList);
